import { tv, type VariantProps } from 'tailwind-variants';

/**
 * Simple Modal theme configuration following HeroUI patterns
 * Supports multiple variants, sizes, and slots customization
 */
export const simpleModalTheme = tv({
  slots: {
    wrapper: [
      'flex',
      'flex-col',
      'z-50',
      'fixed',
      'inset-0',
      'items-center',
      'justify-center',
      'overflow-y-auto',
      'overflow-x-hidden',
    ],
    backdrop: [
      'fixed',
      'inset-0',
      'bg-black/75',
    ],
    base: [
      'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]',
      'rounded-xl',
      'p-7.5',
      'max-w-full',
      'relative',
      'z-10',
      'pointer-events-auto',
      'flex',
      'flex-col',
    ],
    header: [
      'flex',
      'p-0',
      'text-xl',
      'font-semibold',
      'relative',
    ],
    body: [
      'flex',
      'flex-col',
      'p-0',
      'pt-2',
      'gap-0',
    ],
    footer: [
      'flex',
      'flex-row',
      'gap-2',
      'p-0',
      'pt-4',
      'justify-end',
    ],
    closeButton: [
      'absolute',
      'top-1',
      'right-1',
      'z-10',
      'p-3',
      'rounded-none',
      'cursor-pointer',
      'transition-colors',
    ],
  },
  variants: {
    // Color variants following HeroUI pattern
    variant: {
      default: {
        base: [
          'bg-content2',
          'text-content2-foreground',
        ],
        closeButton: [
          'text-default-500',
          'hover:text-default-900',
        ],
      },
      solid: {
        base: [
          'bg-content2',
          'text-content2-foreground',
        ],
        closeButton: [
          'text-default-500',
          'hover:text-default-700',
          'hover:bg-default-100',
        ],
      },
      bordered: {
        base: [
          'bg-content1',
          'text-content1-foreground',
          'border-2',
          'border-default-300',
        ],
        closeButton: [
          'text-default-500',
          'hover:text-default-700',
          'hover:bg-default-100',
        ],
      },
      light: {
        base: [
          'bg-default-50',
          'text-default-900',
        ],
        closeButton: [
          'text-default-400',
          'hover:text-default-600',
          'hover:bg-default-200',
        ],
      },
      flat: {
        base: [
          'bg-default-100',
          'text-default-900',
        ],
        closeButton: [
          'text-default-500',
          'hover:text-default-700',
          'hover:bg-default-200',
        ],
      },
      faded: {
        base: [
          'bg-default-100',
          'text-default-900',
          'border',
          'border-default-300',
        ],
        closeButton: [
          'text-default-500',
          'hover:text-default-700',
          'hover:bg-default-200',
        ],
      },
      shadow: {
        base: [
          'bg-content1',
          'text-content1-foreground',
          'shadow-lg',
        ],
        closeButton: [
          'text-default-500',
          'hover:text-default-700',
          'hover:bg-default-100',
        ],
      },
      primary: {
        base: [
          'bg-primary-50',
          'text-primary-900',
          'border',
          'border-primary-200',
        ],
        header: [
          'text-primary-900',
        ],
        closeButton: [
          'text-primary-500',
          'hover:text-primary-700',
          'hover:bg-primary-100',
        ],
      },
      secondary: {
        base: [
          'bg-secondary-50',
          'text-secondary-900',
          'border',
          'border-secondary-200',
        ],
        header: [
          'text-secondary-900',
        ],
        closeButton: [
          'text-secondary-500',
          'hover:text-secondary-700',
          'hover:bg-secondary-100',
        ],
      },
      success: {
        base: [
          'bg-success-50',
          'text-success-900',
          'border',
          'border-success-200',
        ],
        header: [
          'text-success-900',
        ],
        closeButton: [
          'text-success-500',
          'hover:text-success-700',
          'hover:bg-success-100',
        ],
      },
      warning: {
        base: [
          'bg-warning-50',
          'text-warning-900',
          'border',
          'border-warning-200',
        ],
        header: [
          'text-warning-900',
        ],
        closeButton: [
          'text-warning-500',
          'hover:text-warning-700',
          'hover:bg-warning-100',
        ],
      },
      danger: {
        base: [
          'bg-danger-50',
          'text-danger-900',
          'border',
          'border-danger-200',
        ],
        header: [
          'text-danger-900',
        ],
        closeButton: [
          'text-danger-500',
          'hover:text-danger-700',
          'hover:bg-danger-100',
        ],
      },
    },
    // Placement variants (mirrors HeroUI modal placement)
    placement: {
      auto: { wrapper: 'justify-end sm:justify-center' },
      center: { wrapper: 'justify-center' },
      top: { wrapper: 'justify-start py-16' },
      'top-center': { wrapper: 'justify-start sm:justify-center' },
      bottom: { wrapper: 'justify-end py-16' },
      'bottom-center': { wrapper: 'justify-end sm:justify-center' },
    },
    // Size variants
    size: {
      xs: {
        base: 'w-80 max-w-xs',
      },
      sm: {
        base: 'w-110',
      },
      md: {
        base: 'w-150',
      },
      lg: {
        base: 'w-200',
      },
      xl: {
        base: 'w-300',
      },
      '2xl': {
        base: 'w-[900px] max-w-2xl',
      },
      '3xl': {
        base: 'w-[1000px] max-w-3xl',
      },
      '4xl': {
        base: 'w-[1100px] max-w-4xl',
      },
      '5xl': {
        base: 'w-[1200px] max-w-5xl',
      },
      full: {
        base: 'w-full h-full max-w-none',
        wrapper: 'p-0',
      },
    },
    // Radius variants
    radius: {
      none: {
        base: 'rounded-none',
      },
      sm: {
        base: 'rounded-md',
      },
      md: {
        base: 'rounded-lg',
      },
      lg: {
        base: 'rounded-xl',
      },
    },
    // Shadow variants
    shadow: {
      none: {
        base: 'shadow-none',
      },
      sm: {
        base: 'shadow-sm',
      },
      md: {
        base: 'shadow-md',
      },
      lg: {
        base: 'shadow-lg',
      },
    },
    // Backdrop variants
    backdrop: {
      transparent: {
        backdrop: 'bg-transparent',
      },
      opaque: {
        backdrop: 'bg-black/75',
      },
      blur: {
        backdrop: 'bg-black/50 backdrop-blur-sm',
      },
    },
    // Dark mode support
    isDarkMode: {
      true: {
        base: [
          'dark:bg-grey-900',
          'dark:text-content2-foreground',
        ],
        closeButton: [
          'dark:text-default-400',
          'dark:hover:text-default-200',
        ],
      },
    },
    // Scroll behavior — mirrors HeroUI's scrollBehavior prop
    // 'outside': the wrapper scrolls (default); 'inside': body scrolls, header/footer stay fixed
    scrollBehavior: {
      outside: {},
      inside: {
        wrapper: ['overflow-hidden'],
        base: ['max-h-[calc(100dvh-6rem)]', 'overflow-hidden'],
        body: ['overflow-y-auto', 'flex-1'],
      },
    },
    // Animation disabled
    disableAnimation: {
      true: {},
      false: {
        base: 'transition-all duration-200 ease-in-out',
        backdrop: 'transition-all duration-200 ease-in-out',
        closeButton: 'transition-all duration-150 ease-in-out',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    placement: 'top',
    size: 'md',
    radius: 'lg',
    shadow: 'lg',
    backdrop: 'opaque',
    isDarkMode: false,
    disableAnimation: false,
    scrollBehavior: 'outside',
  },
});

export type SimpleModalVariantProps = VariantProps<typeof simpleModalTheme>;
export type SimpleModalSlots = keyof ReturnType<typeof simpleModalTheme>;

/**
 * Helper function to generate theme classes for simple modal
 */
export const useSimpleModalTheme = (props: SimpleModalVariantProps = {}) => {
  return simpleModalTheme(props);
};
